import { 
  doc as firestoreDoc, 
  getDoc, 
  updateDoc, 
  collection, 
  addDoc, 
  getDocs, 
  deleteDoc, 
  query, 
  orderBy,
  where,
  writeBatch,
  serverTimestamp,
  increment
} from 'firebase/firestore';
import { db, formatDocument } from './firebase';

/**
 * Busca todos os serviços de um salão
 * @param {string} salonId - ID do salão
 * @param {string} categoryId - ID opcional da categoria para filtrar
 * @returns {Promise<Array>} - Lista de serviços
 */
export const getServices = async (salonId, categoryId = null) => {
  if (!salonId) {
    throw new Error('ID do salão é obrigatório');
  }
  
  try {
    let servicesRef;
    
    if (categoryId) {
      servicesRef = query(
        collection(db, 'salons', salonId, 'services'), 
        where('categoria', '==', categoryId),
        orderBy('ordem', 'asc')
      );
    } else {
      servicesRef = query(
        collection(db, 'salons', salonId, 'services'),
        orderBy('ordem', 'asc')
      );
    }
    
    const snapshot = await getDocs(servicesRef);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Erro ao obter serviços:', error);
    throw error;
  }
};

/**
 * Busca um serviço específico
 * @param {string} salonId - ID do salão
 * @param {string} serviceId - ID do serviço
 * @returns {Promise<object|null>} - Dados do serviço ou null se não encontrado
 */
export const getService = async (salonId, serviceId) => {
  if (!salonId) {
    throw new Error('ID do salão é obrigatório');
  }
  
  if (!serviceId) {
    throw new Error('ID do serviço é obrigatório');
  }
  
  try {
    const serviceRef = firestoreDoc(db, `salons/${salonId}/services/${serviceId}`);
    const serviceSnap = await getDoc(serviceRef);
    
    if (serviceSnap.exists()) {
      return {
        id: serviceSnap.id,
        ...serviceSnap.data()
      };
    } else {
      console.error('Serviço não encontrado');
      return null;
    }
  } catch (error) {
    console.error('Erro ao buscar serviço:', error);
    throw error;
  }
};

/**
 * Adiciona um novo serviço
 * @param {string} salonId - ID do salão
 * @param {object} serviceData - Dados do serviço
 * @returns {Promise<object>} - Serviço criado com ID
 */
export const addService = async (salonId, serviceData) => {
  if (!salonId) {
    throw new Error('ID do salão é obrigatório');
  }
  
  if (!serviceData || !serviceData.nome || !serviceData.categoria) {
    throw new Error('Nome e categoria do serviço são obrigatórios');
  }
  
  try {
    const formattedServiceData = {
      ...serviceData,
      preco: Number(serviceData.preco) || 0,
      duracao: Number(serviceData.duracao) || 30,
      setupTime: Number(serviceData.setupTime || 0),
      finishTime: Number(serviceData.finishTime || 0),
      allowsOverlap: Boolean(serviceData.allowsOverlap),
      professionalCount: 0, // Garantir que sempre tenha professionalCount
      criado_em: serverTimestamp(),
      atualizado_em: serverTimestamp()
    };
    
    const servicesRef = collection(db, `salons/${salonId}/services`);
    const docRef = await addDoc(servicesRef, formattedServiceData);
    
    console.log(`[addService] Serviço criado com sucesso: ${docRef.id}`);
    
    return { 
      id: docRef.id,
      salonId, // Incluir salonId para uso em outras partes da aplicação
      ...formattedServiceData
    };
  } catch (error) {
    console.error('Erro ao adicionar serviço:', error);
    throw error;
  }
};

/**
 * Sanitiza os dados do serviço para evitar valores undefined no Firestore
 * @param {Object} serviceData - Dados do serviço a serem sanitizados
 * @returns {Object} - Dados sanitizados
 */
export const sanitizeServiceData = (serviceData) => {
  if (!serviceData) return {};
  
  // Cria uma cópia profunda dos dados para evitar modificar o objeto original
  const sanitized = JSON.parse(JSON.stringify(serviceData));
  
  // Verificar e sanitizar professionalCustomizations
  if (sanitized.professionalCustomizations) {
    Object.keys(sanitized.professionalCustomizations).forEach(profId => {
      const customization = sanitized.professionalCustomizations[profId];
      if (!customization) {
        sanitized.professionalCustomizations[profId] = {
          customPrice: null,
          customDuration: null,
          draftPrice: null,
          draftDuration: null
        };
        return;
      }
      
      // Garantir que todas as propriedades necessárias existam
      sanitized.professionalCustomizations[profId] = {
        customPrice: customization.customPrice === undefined ? null : customization.customPrice,
        customDuration: customization.customDuration === undefined ? null : customization.customDuration,
        draftPrice: customization.draftPrice === undefined ? null : customization.draftPrice,
        draftDuration: customization.draftDuration === undefined ? null : customization.draftDuration
      };
    });
  }
  
  return sanitized;
};

/**
 * Atualiza um serviço existente
 * @param {string} salonId - ID do salão
 * @param {string} serviceId - ID do serviço
 * @param {object} serviceData - Dados do serviço a atualizar
 * @returns {Promise<object>} - Serviço atualizado
 */
export const updateService = async (salonId, serviceId, serviceData) => {
  try {
    console.log('[updateService] Atualizando serviço:', serviceId);
    
    // Validar parâmetros
    if (!salonId || !serviceId || !serviceData) {
      throw new Error('Parâmetros inválidos para atualização de serviço');
    }
    
    // Sanitizar os dados antes de atualizar
    const sanitizedData = sanitizeServiceData(serviceData);
    
    // Verificar se devemos manter o professionalCount atual
    if (sanitizedData.professionalCount === undefined) {
      const serviceRef = firestoreDoc(db, `salons/${salonId}/services/${serviceId}`);
      const serviceSnap = await getDoc(serviceRef);
      
      if (serviceSnap.exists()) {
        const currentData = serviceSnap.data();
        if (currentData.professionalCount !== undefined) {
          console.log('[updateService] Preservando professionalCount atual:', currentData.professionalCount);
          sanitizedData.professionalCount = currentData.professionalCount;
        }
      }
    }
    
    console.log('[updateService] Dados de atualização preparados:', sanitizedData);
    
    // Atualizar documento
    const serviceRef = firestoreDoc(db, `salons/${salonId}/services/${serviceId}`);
    await updateDoc(serviceRef, sanitizedData);
    
    return {
      id: serviceId,
      ...sanitizedData
    };
  } catch (error) {
    console.error('[updateService] Erro ao atualizar serviço:', error);
    throw error;
  }
};

/**
 * Remove um serviço
 * @param {string} salonId - ID do salão
 * @param {string} serviceId - ID do serviço
 * @returns {Promise<boolean>} - True se removido com sucesso
 */
export const deleteService = async (salonId, serviceId) => {
  if (!salonId) {
    throw new Error('ID do salão é obrigatório');
  }
  
  if (!serviceId) {
    throw new Error('ID do serviço é obrigatório');
  }
  
  try {
    console.log(`[deleteService] Iniciando exclusão do serviço: ${serviceId}`);
    
    const batch = writeBatch(db);
    
    // Verificar se o documento existe
    const serviceRef = firestoreDoc(db, `salons/${salonId}/services/${serviceId}`);
    const serviceSnap = await getDoc(serviceRef);
    
    if (!serviceSnap.exists()) {
      console.warn(`[deleteService] Serviço não encontrado: ${serviceId}`);
      return false;
    }
    
    const serviceData = serviceSnap.data();
    console.log(`[deleteService] Serviço encontrado: ${serviceData.nome}`);
    
    // 1. Obter todas as associações deste serviço
    const associationsQuery = query(
      collection(db, `salons/${salonId}/professional_services`),
      where('serviceId', '==', serviceId)
    );
    const associationsSnapshot = await getDocs(associationsQuery);
    
    console.log(`[deleteService] Encontradas ${associationsSnapshot.docs.length} associações para remover`);
    
    // 2. Atualizar contadores de profissionais e remover associações
    const professionalsRef = {};
    const professionalIds = [];
    
    associationsSnapshot.docs.forEach(doc => {
      const data = doc.data();
      batch.delete(doc.ref);
      
      // Armazenar referências para atualizar contadores de profissionais
      if (!professionalsRef[data.professionalId]) {
        professionalsRef[data.professionalId] = firestoreDoc(db, `salons/${salonId}/professionals/${data.professionalId}`);
        professionalIds.push(data.professionalId);
      }
    });
    
    // 3. Decrementar serviceCount para cada profissional
    for (const professionalId in professionalsRef) {
      try {
        // Verificar o profissional antes de atualizar
        const professionalSnap = await getDoc(professionalsRef[professionalId]);
        
        if (professionalSnap.exists()) {
          const professional = professionalSnap.data();
          const currentCount = professional.serviceCount || 0;
          
          // Evitar contador negativo
          const newCount = Math.max(0, currentCount - 1);
          
          console.log(`[deleteService] Atualizando serviceCount para profissional ${professionalId} (${professional.nome || professional.name}): ${currentCount} -> ${newCount}`);
          
          batch.update(professionalsRef[professionalId], {
            serviceCount: newCount,
            updatedAt: serverTimestamp()
          });
        } else {
          console.warn(`[deleteService] Profissional não encontrado para atualização: ${professionalId}`);
        }
      } catch (err) {
        console.error(`[deleteService] Erro ao atualizar contador do profissional ${professionalId}:`, err);
        // Continuar mesmo com erro para não bloquear a operação completa
      }
    }
    
    // 4. Remover o serviço
    batch.delete(serviceRef);
    
    console.log(`[deleteService] Executando batch com ${Object.keys(professionalsRef).length} atualizações de profissionais`);
    await batch.commit();
    
    console.log(`[deleteService] Serviço ${serviceId} removido com sucesso`);
    return true;
  } catch (error) {
    console.error('[deleteService] Erro ao excluir serviço:', error);
    throw error;
  }
};

/**
 * Atualiza a ordem de exibição dos serviços
 * @param {string} salonId - ID do salão
 * @param {Array} servicesOrder - Array com IDs dos serviços na ordem desejada
 * @returns {Promise<boolean>} - True se atualizado com sucesso
 */
export const updateServicesOrder = async (salonId, servicesOrder) => {
  if (!salonId) {
    throw new Error('ID do salão é obrigatório');
  }
  
  if (!servicesOrder || !Array.isArray(servicesOrder) || servicesOrder.length === 0) {
    throw new Error('Lista de serviços inválida');
  }
  
  // Verificar se todos os IDs são válidos
  if (servicesOrder.some(id => !id)) {
    throw new Error('A lista contém IDs de serviço inválidos');
  }
  
  try {
    // Usar uma operação em lote para atualizar vários serviços
    const batch = writeBatch(db);
    
    // Verificar se todos os documentos existem
    for (const serviceId of servicesOrder) {
      const serviceRef = firestoreDoc(db, `salons/${salonId}/services/${serviceId}`);
      const serviceSnap = await getDoc(serviceRef);
      
      if (!serviceSnap.exists()) {
        throw new Error(`Serviço não encontrado: ${serviceId}`);
      }
    }
    
    servicesOrder.forEach((serviceId, index) => {
      const serviceRef = firestoreDoc(db, `salons/${salonId}/services/${serviceId}`);
      batch.update(serviceRef, { ordem: index, atualizado_em: serverTimestamp() });
    });
    
    await batch.commit();
    return true;
  } catch (error) {
    console.error('Erro ao atualizar ordem dos serviços:', error);
    throw error;
  }
};

/**
 * Obtém o salonId a partir de um serviço específico
 * Esta função é útil quando você tem apenas um serviceId e precisa descobrir a qual salão ele pertence
 * 
 * @param {string} serviceId - ID do serviço
 * @returns {Promise<string|null>} - salonId ou null se não encontrado
 */
export const getSalonIdFromService = async (serviceId) => {
  if (!serviceId) {
    console.error('[getSalonIdFromService] serviceId é obrigatório');
    return null;
  }
  
  try {
    console.log(`[getSalonIdFromService] Buscando salonId para o serviço: ${serviceId}`);
    
    // Verificar nos salões disponíveis
    const salonsRef = collection(db, 'salons');
    const salonsSnapshot = await getDocs(salonsRef);
    
    const searchPromises = [];
    
    // Para cada salão, verificar se contém o serviço
    salonsSnapshot.forEach(salon => {
      const salonId = salon.id;
      const serviceRef = firestoreDoc(db, `salons/${salonId}/services/${serviceId}`);
      
      // Criar uma promessa para cada verificação
      searchPromises.push(
        getDoc(serviceRef).then(serviceSnap => {
          if (serviceSnap.exists()) {
            return salonId;
          }
          return null;
        })
      );
    });
    
    // Executar todas as verificações em paralelo
    const results = await Promise.all(searchPromises);
    
    // Filtrar resultados nulos e pegar o primeiro válido
    const foundSalonId = results.find(id => id !== null);
    
    if (foundSalonId) {
      console.log(`[getSalonIdFromService] salonId encontrado: ${foundSalonId}`);
      return foundSalonId;
    }
    
    console.error(`[getSalonIdFromService] Nenhum salonId encontrado para o serviço: ${serviceId}`);
    return null;
  } catch (error) {
    console.error('[getSalonIdFromService] Erro:', error);
    return null;
  }
}; 